import { InputNumber as InputNumberAntd, InputNumberProps, Tooltip } from 'antd';
import 'antd/es/input-number/style/index';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import IconFont from '@meitu/xdesign-icon-font';
import useMouseWheel from './useMouseWheel';
import './index.less';

export interface IInputNumberOldProps extends Omit<InputNumberProps<number>, 'onChange'> {
    /**
     * 是否展示上下箭头
     */
    showStepBtn?: boolean;
    /** 启动鼠标滚动 */
    enableMouseWheel?: boolean;
    /** 鼠标滚动变化率(不支持状态更新) */
    mouseWheelRatio?: number;
    tip?: string;
    value?: number | null;
    keyboard?: boolean;
    /** 带有后缀图标的input (antd5.20.0以上版本才有, 这里用的antd版本太低, 自己实现) */
    suffix?: React.ReactNode;
    /** 带有前缀图标的input */
    beforeSuffix?: React.ReactNode;
    /**
     * antd的value是支持undefined/null, 但是saas-ui的这个组件不支持, 加这个参数, 让它支持一下.
     * 因为有些场景不需要默认值, 需要保留undefined/null
     */
    shouldReserveValueUndefinedOrNull?: boolean;
    onChange?: (value: number | null, isMouseWheel: boolean) => void;
    /** 一次onChange后会跟随一次onChangeAfter, 如果需要用到正确的onChangeAfter逻辑, 请替换成v2 */
    onChangeAfter?: (value: number | null) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
    onMouseWheelEnd?: () => void;
}

/**
 * 判断是否是平板设备（SSR 安全）
 */
const isPad = () => {
    // SSR 环境（无 window/navigator/screen）
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || typeof screen === 'undefined') {
        return true; // SSR 下默认返回 true
    }
    const ua = navigator.userAgent.toLowerCase();
    const maxTouch = navigator.maxTouchPoints || 0;
    const dpr = window.devicePixelRatio || 1;
    const width = Math.min(window.screen.width, window.screen.height);

    // 1. iPad / iPadOS（iPadOS 13+ 会伪装成 Mac）
    if (/ipad/.test(ua) || (/macintosh/.test(ua) && maxTouch > 1)) {
        return true;
    }

    // 2. Android 平板（非手机）
    if (/android/.test(ua) && !/mobile/.test(ua)) {
        return true;
    }

    // 3. Windows / Linux / X11 平板（触屏设备）
    if ((/windows/.test(ua) || /linux/.test(ua) || /x11/.test(ua)) && maxTouch > 1) {
        // 增加 DPI 判断：一般平板 >= 1.5，触屏电脑多为 1.0~1.25
        const isHighDPI = dpr >= 1.5;
        // 宽度小于 1280 且高 DPI 视作平板，否则视作触屏电脑
        return width < 1280 && isHighDPI;
    }

    return false;
};

/** @deprecated 旧版本的onChangeAfter时机不对, 请替换成v2 (如果有使用到onChangeAfter, 替换后可能会影响业务使用, 需要让测试过一下). v2用法: <InputNumber v2 ... /> */
const InputNumberOld: React.FC<IInputNumberOldProps> = ({
    showStepBtn = true,
    enableMouseWheel = true,
    mouseWheelRatio = 1,
    tip,
    max: maxProp,
    min: minProp,
    value,
    precision,
    style = {},
    onChange,
    onChangeAfter,
    onMouseWheelEnd,
    keyboard = true,
    suffix,
    beforeSuffix,
    shouldReserveValueUndefinedOrNull = false,
    disabled = false,
    onFocus,
    onBlur,
    ...resetProps
}) => {
    const max = maxProp ?? Number.MAX_SAFE_INTEGER;
    const min = minProp ?? Number.MIN_SAFE_INTEGER;
    const timerRef = useRef<number>();
    const wrapperElRef = useRef<HTMLDivElement>(null);
    const inputElRef = useRef<HTMLInputElement>(null);
    const [focus, setFocus] = useState(false);

    if (!shouldReserveValueUndefinedOrNull && value === null) {
        value = 1;
    }

    const checkValue = (num: number) => {
        // 取区间值
        let val = Math.min(num, max);
        val = Math.max(val, min);
        return val;
    };

    // 处理输入框
    const handleAddOrMinus = (mode: 'add' | 'minus') => {
        if (disabled) return;
        if (shouldReserveValueUndefinedOrNull && (value === null || value === undefined)) {
            onChange?.(minProp ?? 1, false);
            onChangeAfter?.(minProp ?? 1);
            return;
        }

        const diff = mode === 'add' ? 1 : -1;
        const val = checkValue((value as number) + diff);
        onChange?.(val, false);
        onChangeAfter?.(val);
    };

    const handleOnInput = (newVal: string) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            if (shouldReserveValueUndefinedOrNull && (newVal === '' || isNaN(+newVal))) {
                if (value !== null && value !== undefined) {
                    onChange?.(null, false);
                }
                clearTimeout(timerRef.current);
                return;
            }

            // 如果删除所有内容 就不更新change事件 其他逻辑不变
            if (newVal === '') {
                clearTimeout(timerRef.current);
                return;
            }

            let num = +newVal;

            if (isNaN(num)) {
                num = value ?? 1;
            }

            let result = checkValue(num);
            if (result < min) {
                result = min;
            }

            if (precision === undefined) {
                onChange?.(result, false);
                onChangeAfter?.(result);
            } else {
                num = num ? num : 0;
                const val = checkValue(+num.toFixed(precision || 0));
                onChange?.(val, false);
                onChangeAfter?.(val);
            }

            clearTimeout(timerRef.current);
        }, 500) as unknown as number;
    };

    // 键盘操作
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!keyboard) {
            return;
        }
        if (e.keyCode === 38) {
            handleAddOrMinus('add');
        } else if (e.keyCode === 40) {
            handleAddOrMinus('minus');
        }
    };

    if (!shouldReserveValueUndefinedOrNull) {
        value = value ? value : 0;
    }

    const mouseWheelHook = useMouseWheel({
        wrapperElRef,
        inputElRef,
        max,
        min,
        inputValue: value ?? 1,
        onChangeInputValue: (value) => onChange?.(value, true),
        onChangeAfter,
        // 在 iPad 设备上禁用鼠标滚动
        enableMouseWheel: isPad() ? false : enableMouseWheel,
        onMouseWheelEnd,
        mouseWheelRatio,
        disabled,
    });

    const renderEle = () => {
        return (
            <div
                ref={wrapperElRef}
                className={classnames('mt-input-number', {
                    'mt-input-number--focused': focus,
                    'mt-input-number--disabled': disabled,
                })}
                style={style}
                onClick={mouseWheelHook.onWrapperClick}
                onMouseDown={mouseWheelHook.onWrapperMouseDown}
                onTouchStart={mouseWheelHook.onWrapperTouchStart}
            >
                {!!beforeSuffix && <div className="input-addon-before">{beforeSuffix}</div>}
                <InputNumberAntd
                    ref={inputElRef}
                    value={
                        shouldReserveValueUndefinedOrNull
                            ? value
                            : checkValue(+(value as number).toFixed(precision || 0))
                    }
                    max={max}
                    min={min}
                    bordered={false}
                    stringMode
                    keyboard={keyboard}
                    disabled={disabled}
                    {...resetProps}
                    className="m-input-container"
                    onInput={handleOnInput}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                        setFocus(true);
                        e.target.select();
                        onFocus?.(e);
                    }}
                    onBlur={(e) => {
                        mouseWheelHook.onInputBlur();
                        setFocus(false);
                        onBlur?.(e);
                    }}
                />
                <div className="input-addon-after" onClick={(e) => e.stopPropagation()}>
                    {!!suffix && <div className="input-suffix">{suffix}</div>}
                    {showStepBtn && (
                        <div className={classnames('arrows', disabled && 'disabled')}>
                            <div className="top" onClick={() => handleAddOrMinus('add')}>
                                <IconFont fontSize={10} name="ChevronUpBold" />
                            </div>
                            <div className="bottom" onClick={() => handleAddOrMinus('minus')}>
                                <IconFont fontSize={10} name="ChevronDownBold" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return <Tooltip title={tip || ''}>{renderEle()}</Tooltip>;
};

export default InputNumberOld;
